import { testId } from '../../utils';
import { Iconography } from './Iconography';
import * as SelectInput from '@radix-ui/react-select';
import clsx from 'clsx';
import React from 'react';

export interface SelectData {
  label?: React.ReactNode;
  value: string;
  disabled?: boolean;
}

interface SelectProps<T extends SelectData> {
  data: T[];
  onChange: (value: string) => void;
  value?: T['value'];
  defaultValue?: T['value'];
  placeholder?: string;
  invalid?: boolean;
  className?: string;
  'data-testid': string;
}

export const Select = React.forwardRef(
  (
    {
      data,
      value,
      onChange,
      defaultValue,
      placeholder,
      invalid,
      className,
      'data-testid': dataTestId,
    }: SelectProps<SelectData>,
    ref: React.ForwardedRef<HTMLButtonElement>,
  ) => {
    return (
      <SelectInput.Root
        defaultValue={defaultValue}
        onValueChange={onChange}
        value={value}
      >
        <SelectInput.Trigger
          data-value={value}
          {...testId(dataTestId)}
          ref={ref}
          className={clsx(
            'inline-flex items-center justify-between w-full h-12 px-3',
            'border border-gray rounded ring-offset-white focus:outline-blue-active',
            'data-[state=open]:outline-2 data-[state=open]:outline data-[state=open]:outline-blue-active',
            'data-[state=open]:-outline-offset-2 group',
            {
              'border-red-dark bg-red-light': invalid,
              'bg-gray-lightest': !value,
              'bg-white': !!value,
            },
            className,
          )}
        >
          <div className="overflow-hidden whitespace-nowrap overflow-ellipsis">
            <SelectInput.Value
              placeholder={placeholder}
              {...testId(dataTestId, 'value')}
            />
          </div>
          <Iconography
            variant="expand_more"
            style="rounded"
            className=" transition-transform duration-200 group-data-[state=open]:-rotate-180"
          />
        </SelectInput.Trigger>

        <SelectInput.Portal>
          <SelectInput.Content
            {...testId(dataTestId, 'content')}
            position="popper"
            className={clsx(
              'select-content-width-full mt-1 max-h-60 w-auto overflow-scroll',
              'bg-white rounded z-50 border-2 border-blue-active',
            )}
          >
            <SelectInput.ScrollUpButton
              {...testId(dataTestId, 'scroll-up')}
              className="flex items-center justify-center h-6 cursor-pointer"
            >
              <Iconography
                variant="expand_more"
                style="rounded"
                className="rotate-180"
              />
            </SelectInput.ScrollUpButton>

            <SelectInput.Viewport {...testId(dataTestId, 'viewport')}>
              {data.map((item) => (
                <SelectInput.Item
                  className={clsx(
                    'cursor-pointer py-2 px-3',
                    'data-[state=checked]:bg-blue-active data-[state=checked]:text-white',
                    'focus:outline-none hover:bg-gray-lightest focus:bg-gray-lightest',
                    { 'text-gray-300': item.disabled },
                  )}
                  {...testId(dataTestId, 'item')}
                  data-value={item.value}
                  key={item.value}
                  value={item.value}
                  disabled={item.disabled}
                >
                  <SelectInput.ItemText {...testId(dataTestId, 'item', 'text')}>
                    {item.label || item.value}
                  </SelectInput.ItemText>
                </SelectInput.Item>
              ))}
            </SelectInput.Viewport>

            <SelectInput.ScrollDownButton
              {...testId(dataTestId, 'scroll-down')}
              className="flex items-center justify-center h-6 cursor-pointer"
            >
              <Iconography variant="expand_more" />
            </SelectInput.ScrollDownButton>
          </SelectInput.Content>
        </SelectInput.Portal>
      </SelectInput.Root>
    );
  },
);

Select.displayName = 'Select';
